import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Loader2, Download } from 'lucide-react';
import { Button } from '../ui/button';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtEUR(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
}
function fmtNum(n) {
  return new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
}

/**
 * Renders the SEA PDF preview from /api/sea-reports/{id}/pdf-data
 * Or, if `publicData` prop provided, uses it directly (public share link path).
 *
 * Provides a "Download PDF" button using html2canvas + jsPDF.
 */
export default function SEAReportPdfPreview({ reportId, publicData }) {
  const [data, setData] = useState(publicData || null);
  const [loading, setLoading] = useState(!publicData);
  const [generating, setGenerating] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (publicData || !reportId) return;
    axios.get(`${API}/sea-reports/${reportId}/pdf-data`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(e => toast.error(e.response?.data?.detail || 'Erreur'))
      .finally(() => setLoading(false));
  }, [reportId, publicData]);

  const downloadPdf = async () => {
    if (!printRef.current) return;
    setGenerating(true);
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const filename = `${(data?.report?.title || 'rapport-sea').replace(/[^a-z0-9]/gi, '-')}.pdf`;
      pdf.save(filename);
      toast.success('PDF téléchargé');
    } catch (e) {
      toast.error('Erreur génération PDF: ' + (e.message || ''));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#002FA7]" /></div>;
  if (!data) return <div className="py-8 text-center text-neutral-500">Aucune donnée</div>;

  const { report, client, summary, daily, top_campaigns, top_keywords, crm_roi, error } = data;
  const dailyChart = (daily || []).map(d => ({ date: d.date.slice(5), cost: d.cost, clicks: d.clicks, conv: d.conversions }));

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={downloadPdf} disabled={generating} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="sea-report-pdf-download">
          {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Télécharger PDF
        </Button>
      </div>

      <div ref={printRef} className="bg-white p-8 rounded-lg" style={{ width: '794px', margin: '0 auto', fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="sea-pdf-content">
        {/* HEADER */}
        <div className="flex items-start justify-between border-b-2 border-[#002FA7] pb-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-orange-600 font-bold">Rapport SEA · Google Ads</div>
            <h1 className="text-2xl font-bold text-neutral-900 mt-1">{report.title}</h1>
            <p className="text-sm text-neutral-600">{client.name} · {report.period_start} → {report.period_end}</p>
          </div>
          <div className="text-right text-xs text-neutral-500">
            Généré le {new Date().toLocaleDateString('fr-FR')}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 mb-4">{error}</div>}

        {/* KPIs */}
        <h2 className="text-base font-bold text-neutral-900 mb-2">📊 Indicateurs clés</h2>
        <div className="grid grid-cols-4 gap-3 mb-6">
          <Kpi label="Impressions" value={fmtNum((summary || {}).impressions)} />
          <Kpi label="Clics" value={fmtNum((summary || {}).clicks)} />
          <Kpi label="Coût" value={fmtEUR((summary || {}).cost)} accent />
          <Kpi label="Conversions" value={fmtNum((summary || {}).conversions)} />
          <Kpi label="CTR" value={`${(((summary || {}).ctr || 0) * 100).toFixed(2)}%`} />
          <Kpi label="CPC moyen" value={fmtEUR((summary || {}).average_cpc)} />
          <Kpi label="CPA" value={fmtEUR((summary || {}).cost_per_conversion)} />
          <Kpi label="ROAS" value={`${(((summary || {}).roas || 0)).toFixed(2)}x`} accent />
        </div>

        {/* Closed-loop ROI */}
        {crm_roi && crm_roi.won_count > 0 && (
          <>
            <h2 className="text-base font-bold text-neutral-900 mb-2">💰 ROI réel · Closed-loop CRM</h2>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4 mb-6">
              <div className="grid grid-cols-3 gap-4">
                <div><div className="text-xs text-emerald-700 uppercase">Revenu attribué</div><div className="text-2xl font-bold text-emerald-700">{fmtEUR(crm_roi.revenue)}</div></div>
                <div><div className="text-xs text-emerald-700 uppercase">Ventes</div><div className="text-2xl font-bold text-emerald-700">{crm_roi.won_count}</div></div>
                <div><div className="text-xs text-emerald-700 uppercase">ROI net</div><div className="text-2xl font-bold text-emerald-700">{fmtEUR((crm_roi.revenue || 0) - ((summary || {}).cost || 0))}</div></div>
              </div>
            </div>
          </>
        )}

        {/* Daily evolution */}
        {dailyChart.length > 0 && (
          <>
            <h2 className="text-base font-bold text-neutral-900 mb-2">📈 Évolution quotidienne</h2>
            <div className="bg-white border border-neutral-200 rounded p-3 mb-6">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={dailyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                  <XAxis dataKey="date" fontSize={9} stroke="#9ca3af" />
                  <YAxis fontSize={9} stroke="#9ca3af" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="cost" stroke="#ea580c" strokeWidth={2} dot={false} name="Coût (€)" />
                  <Line type="monotone" dataKey="clicks" stroke="#002FA7" strokeWidth={2} dot={false} name="Clics" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* Top campaigns */}
        {top_campaigns && top_campaigns.length > 0 && (
          <>
            <h2 className="text-base font-bold text-neutral-900 mb-2">🎯 Top campagnes</h2>
            <table className="w-full text-xs mb-6 border-collapse">
              <thead className="bg-neutral-100">
                <tr><th className="text-left p-2">Campagne</th><th className="text-right p-2">Impressions</th><th className="text-right p-2">Clics</th><th className="text-right p-2">Coût</th><th className="text-right p-2">Conv.</th></tr>
              </thead>
              <tbody>
                {top_campaigns.map((c) => (
                  <tr key={c.id || c.name} className="border-b border-neutral-100">
                    <td className="p-2 font-medium">{c.name}</td>
                    <td className="p-2 text-right">{fmtNum(c.impressions)}</td>
                    <td className="p-2 text-right">{fmtNum(c.clicks)}</td>
                    <td className="p-2 text-right font-medium">{fmtEUR(c.cost)}</td>
                    <td className="p-2 text-right">{fmtNum(c.conversions)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Top keywords */}
        {top_keywords && top_keywords.length > 0 && (
          <>
            <h2 className="text-base font-bold text-neutral-900 mb-2">🔑 Top mots-clés</h2>
            <table className="w-full text-xs mb-6 border-collapse">
              <thead className="bg-neutral-100">
                <tr><th className="text-left p-2">Mot-clé</th><th className="text-left p-2">Match</th><th className="text-right p-2">Imp.</th><th className="text-right p-2">Clics</th><th className="text-right p-2">Coût</th></tr>
              </thead>
              <tbody>
                {top_keywords.slice(0, 10).map((k) => (
                  <tr key={`${k.text || k.keyword || ''}-${k.match_type || ''}`} className="border-b border-neutral-100">
                    <td className="p-2">{k.text || k.keyword || '—'}</td>
                    <td className="p-2 text-neutral-500">{k.match_type || '—'}</td>
                    <td className="p-2 text-right">{fmtNum(k.impressions)}</td>
                    <td className="p-2 text-right">{fmtNum(k.clicks)}</td>
                    <td className="p-2 text-right">{fmtEUR(k.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Summary text */}
        {report.summary && (
          <>
            <h2 className="text-base font-bold text-neutral-900 mb-2">📝 Synthèse & recommandations</h2>
            <div className="bg-neutral-50 border border-neutral-200 rounded p-3 text-sm whitespace-pre-wrap text-neutral-800">
              {report.summary}
            </div>
          </>
        )}

        <div className="mt-8 pt-4 border-t border-neutral-200 text-center text-[10px] text-neutral-400">
          Rapport généré automatiquement · {new Date().toLocaleString('fr-FR')}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`border rounded p-2.5 ${accent ? 'border-orange-300 bg-orange-50' : 'border-neutral-200 bg-white'}`}>
      <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-medium">{label}</div>
      <div className={`text-base font-bold mt-0.5 ${accent ? 'text-orange-700' : 'text-neutral-900'}`}>{value}</div>
    </div>
  );
}
